import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { licenseCounters } from '../utils/licenseDisplay';

const LICENSE_API = 'https://glitchcloud.app/license-api';


type AuthState = 'loading' | 'success' | 'no_account' | 'error';

interface Props {
  onSuccess?: () => void;
}

const TelegramMiniAppAuth: React.FC<Props> = ({ onSuccess }) => {
  const [authState, setAuthState] = useState<AuthState>('loading');
  const [errorMsg, setErrorMsg] = useState('');
  const navigate = useNavigate();

  // Données Telegram disponibles immédiatement, avant l'appel réseau
  const tgUserImmediate = window.Telegram?.WebApp?.initDataUnsafe?.user;

  useEffect(() => {
    const twa = window.Telegram?.WebApp;

    if (!twa?.initData) {
      setAuthState('error');
      setErrorMsg('Not running inside a Telegram MiniApp.');
      return;
    }

    twa.ready();
    twa.expand();

    const authenticate = async () => {
      try {
        const tgUser = twa.initDataUnsafe?.user;
        // GET simple = pas de preflight OPTIONS → contourne les problèmes CORS nginx
        const params = new URLSearchParams({
          telegram_id: String(tgUser?.id ?? ''),
          username:    tgUser?.username   ?? '',
          first_name:  tgUser?.first_name ?? '',
        });
        const resp = await fetch(`${LICENSE_API}/auth/telegram-id?${params}`, {
          method: 'GET',
        });

        const data = await resp.json();

        if (!resp.ok || !data.valid) {
          if (resp.status === 404) {
            setAuthState('no_account');
          } else {
            setAuthState('error');
            setErrorMsg(data.message ?? data.error ?? 'Authentication failed.');
          }
          return;
        }

        // Build session identical to LicenseAuth shape
        const hwid = `tg_${twa.initDataUnsafe?.user?.id ?? 'unknown'}`;

        const licenseInfo = {
          code:          data.licenseKey,
          type:          data.type,
          username:      data.username,
          expiryDate:    data.expiryDate,
          isValid:       true,
          hwid,
          vpsIp:         data.vpsIp,
          vpsPort:       data.vpsPort,
          vpsIp2:        data.vpsIp2        ?? null,
          vpsPort2:      data.vpsPort2      ?? null,
          vpsList:       Array.isArray(data.vpsList) ? data.vpsList : [],
          grabber:       data.grabber       ?? false,
          mailer:        data.mailer        ?? false,
          debouncer:     data.debouncer     ?? false,
          stripe:        data.stripe        ?? false,
          premiumProxy:  data.premiumProxy  ?? false,
          currentLevel:  data.currentLevel  ?? null,
          currentLevelXp: data.currentLevelXp ?? 0,
          nextLevelXp:   data.nextLevelXp   ?? 0,
          rank:          data.rank          ?? null,
          totalScan:     data.totalScan     ?? 0,
          isAdmin:       data.isAdmin       ?? false,
          telegramUser:  data.telegramUser  ?? null,
          jwt:           data.jwt           ?? null,
          ...licenseCounters(data),
        };

        const userData = {
          id:               hwid,
          name:             data.username ?? data.telegramUser?.first_name ?? 'Operator',
          license:          data.licenseKey,
          telegramPhotoUrl: data.telegramUser?.photo_url ?? null,
        };

        // Merge into multiple_licenses (keep existing entries)
        const raw = localStorage.getItem('multiple_licenses');
        const all: any[] = raw ? JSON.parse(raw) : [];
        const deactivated = all.map((l) => ({ ...l, isActive: false }));
        const existingIdx = deactivated.findIndex((l) => l.code === data.licenseKey);
        const newEntry = { ...licenseInfo, id: hwid, isActive: true, authMethod: 'telegram' };

        if (existingIdx >= 0) {
          deactivated[existingIdx] = { ...deactivated[existingIdx], ...newEntry };
        } else {
          deactivated.push(newEntry);
        }

        localStorage.setItem('multiple_licenses', JSON.stringify(deactivated));
        localStorage.setItem('license_user',  JSON.stringify(userData));
        localStorage.setItem('user_license',  JSON.stringify(licenseInfo));
        window.dispatchEvent(new CustomEvent('licenseActivated', { detail: licenseInfo }));

        setAuthState('success');
        onSuccess?.();
        navigate('/dashboard');
      } catch (err: any) {
        setAuthState('error');
        setErrorMsg(err?.message ?? String(err) ?? 'Network error');
      }
    };

    authenticate();
  }, [navigate, onSuccess]);

  if (authState === 'loading') {
    const photoUrl = (tgUserImmediate as any)?.photo_url ?? null;
    const firstName = tgUserImmediate?.first_name ?? '';
    const username  = (tgUserImmediate as any)?.username ?? '';
    const tgId      = tgUserImmediate?.id ?? '';

    return (
      <motion.div
        className="flex flex-col items-center gap-5 py-8"
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35 }}
      >
        {/* Avatar */}
        <div style={{ position: 'relative', width: 72, height: 72 }}>
          {photoUrl ? (
            <img
              src={photoUrl}
              alt=""
              style={{
                width: 72, height: 72,
                borderRadius: '50%',
                objectFit: 'cover',
                border: '2px solid #e53935',
              }}
            />
          ) : (
            <div style={{
              width: 72, height: 72,
              borderRadius: '50%',
              background: 'linear-gradient(135deg,#1a1a2e,#2d0a0a)',
              border: '2px solid #e53935',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 26, fontWeight: 700, color: '#e53935',
            }}>
              {firstName.slice(0, 1).toUpperCase()}
            </div>
          )}
          {/* Spinner ring */}
          <div style={{
            position: 'absolute', inset: -5,
            borderRadius: '50%',
            border: '2px solid transparent',
            borderTopColor: '#e53935',
            animation: 'spin 1s linear infinite',
          }} />
        </div>

        {/* Infos */}
        <div style={{ textAlign: 'center' }}>
          {firstName && (
            <p style={{ color: '#fff', fontWeight: 700, fontSize: 16, margin: 0 }}>
              {firstName}{username ? ` @${username}` : ''}
            </p>
          )}
          {tgId && (
            <p style={{ color: '#5a5a6a', fontSize: 11, margin: '3px 0 0' }}>
              ID {tgId}
            </p>
          )}
        </div>

        <p style={{ color: '#737373', fontSize: 13, margin: 0 }}>Connexion en cours…</p>
      </motion.div>
    );
  }

  if (authState === 'no_account') {
    return (
      <div className="flex flex-col items-center gap-4 py-10 text-center">
        <span style={{ fontSize: 40 }}>🔗</span>
        <p className="text-white font-semibold">Aucune licence liée</p>
        <p className="text-sm text-[#737373] max-w-xs">
          Connectez-vous avec votre clé de licence sur le site, puis liez votre
          compte Telegram dans les paramètres du dashboard.
        </p>
      </div>
    );
  }

  if (authState === 'error') {
    return (
      <motion.div
        initial={{ opacity: 0, y: -6 }}
        animate={{ opacity: 1, y: 0 }}
        style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: '16px 0' }}
      >
        <p style={{ color: '#e53935', fontWeight: 600, fontSize: 14, margin: 0 }}>Erreur de connexion</p>
        <pre style={{
          background: '#111', border: '1px solid #2a2a3a', borderRadius: 6,
          padding: '10px 12px', color: '#f87171', fontSize: 11,
          wordBreak: 'break-all', whiteSpace: 'pre-wrap', margin: 0,
        }}>
          {errorMsg}
        </pre>
        <p style={{ color: '#5a5a6a', fontSize: 11, margin: 0 }}>
          API: {LICENSE_API}
        </p>
      </motion.div>
    );
  }

  return null;
};

export default TelegramMiniAppAuth;
